import { preferencesReducer } from '@core/store/entity/preferences/PreferencesReducer';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { useEntityThunk } from '@hwm/ui-lib';
import { useCallback } from 'react';
import { preferencesUiSlice } from '../../stores/PreferencesUiReducer';
import { useGetById } from '@core/store/hooks/entityHooks';
import { selectPreferencesState } from '@core/store/entity/preferences/PreferencesSelector';
import { PreferencesModel } from '@models/Preferences';
import { isNil } from 'lodash';
import { useNotifications } from '@shared/hooks/useNotifications';
import { t } from '@lingui/macro';

export const usePreferencesDataSource = () => {
  const dispatch = useAppDispatch();

  const { handleSuccess, handleError } = useNotifications();

  const { userPreferences, loading } = useAppSelector(selectPreferencesState);
  const accountPreferences = useGetById(
    preferencesReducer.entityAdapter,
    preferencesReducer.slice.name,
    (state) => state['preferences-ui'].loadedDetailsId
  );

  const [getAccountPreferences] = useEntityThunk(
    preferencesReducer.thunks.getAccountPreferences,
    'error msg',
    useCallback(
      (response) => {
        dispatch(
          preferencesUiSlice.actions.setDetails({
            id: response.accountId.toString(),
          })
        );
      },
      [dispatch]
    )
  );

  const handleGetAccountPreferences = useCallback(
    (accountId: number) => {
      if (!accountId) return;

      getAccountPreferences({ accountId });
    },
    [getAccountPreferences]
  );

  const handleSavePreferences = useCallback(
    (data: PreferencesModel, accountId: number | null, cb: () => void) => {
      if (!data) return;

      const thunk = isNil(accountId)
        ? preferencesReducer.thunks.saveUserPreferences
        : preferencesReducer.thunks.saveAccountPreferences;

      dispatch(thunk({ accountId, data }))
        .then(() => {
          cb();
          handleSuccess(t`Successfully saved preferences`, t`Preferences`);
        })
        .catch((err) => {
          handleError(err, t`Preferences`, t`There was a problem while trying to save preferences`);
        });
    },
    [dispatch, handleSuccess, handleError]
  );

  return {
    accountPreferences,
    userPreferences,
    getAccountPreferences: handleGetAccountPreferences,
    savePreferences: handleSavePreferences,
    loading,
  };
};
